import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Download, FileText, History, Pencil, Plus } from "lucide-react";
import { DashboardLayout, Panel, StatusBadge } from "@/components/DashboardLayout";
import { api, apiError, ID_TYPES, naira, NG_STATES } from "@/lib/api";
import { FileField, GeoField } from "@/components/KycFields";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { LocationPin } from "@/components/LocationPin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NAV = [
  { to: "/dashboard/master-client", label: "Overview", testId: "nav-overview" },
  { to: "/beneficiaries", label: "Beneficiaries", testId: "nav-beneficiaries" },
  { to: "/witnesses", label: "Witnesses", testId: "nav-witnesses" },
  { to: "/marketplace", label: "Find providers", testId: "nav-marketplace" },
  { to: "/bookings", label: "Bookings", testId: "nav-bookings" },
];

const EMPTY_B = {
  full_name: "", contact_number: "", medical_condition: "", service_address: "", city: "", state: "",
  relationship: "", photograph_file_id: "", geolocation: null,
};

export function Beneficiaries() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY_B);
  const [timeline, setTimeline] = useState(null);

  const openTimeline = async (row) => {
    try {
      const { data } = await api.get(`/api/beneficiaries/${row.id}/timeline`);
      setTimeline(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = () => api.get("/api/beneficiaries").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...f, photograph_file_id: f.photograph_file_id || null };
    try {
      if (editId) await api.put(`/api/beneficiaries/${editId}`, payload);
      else await api.post("/api/beneficiaries", payload);
      toast.success(editId ? "Beneficiary updated" : "Beneficiary added");
      setOpen(false);
      setEditId(null);
      setF(EMPTY_B);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const archive = async (id) => {
    try {
      await api.post(`/api/beneficiaries/${id}/archive`);
      toast.success("Beneficiary archived");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setF({ ...EMPTY_B, ...row });
    setOpen(true);
  };

  const valid = f.full_name && f.contact_number && f.medical_condition && f.service_address && f.city && f.state && f.relationship;

  return (
    <DashboardLayout title="Beneficiary profiles" subtitle="The people who receive care. Medical details are private to you and authorised reviewers." nav={NAV}>
      <Panel
        title={`${rows.length} beneficiar${rows.length === 1 ? "y" : "ies"}`}
        testId="beneficiaries-panel"
        action={
          <Button className="rounded-full bg-emerald-900 hover:bg-emerald-800" onClick={() => { setEditId(null); setF(EMPTY_B); setOpen(true); }} data-testid="add-beneficiary-btn">
            <Plus className="mr-2 h-4 w-4" /> Add beneficiary
          </Button>
        }
      >
        {rows.length ? (
          <ul className="divide-y divide-slate-100" data-testid="beneficiaries-list">
            {rows.map((b) => (
              <li key={b.id} className="flex flex-wrap items-start justify-between gap-4 py-5" data-testid={`beneficiary-row-${b.id}`}>
                <div className="min-w-0">
                  <p className="font-display font-bold text-slate-900">{b.full_name}</p>
                  <p className="text-sm text-slate-600">{b.relationship} · {b.contact_number}</p>
                  <p className="mt-1 text-sm text-slate-500">{b.medical_condition}</p>
                  <p className="mt-1 text-xs text-slate-400">{b.service_address}, {b.city}, {b.state}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openTimeline(b)} data-testid={`timeline-beneficiary-${b.id}`}>
                    <History className="mr-1.5 h-3.5 w-3.5" /> Timeline
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => startEdit(b)} data-testid={`edit-beneficiary-${b.id}`}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => archive(b.id)} data-testid={`archive-beneficiary-${b.id}`}>
                    <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No beneficiaries yet. Your KYC must be verified before adding one.</p>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit beneficiary" : "New beneficiary"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input className="mt-1.5" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="ben-name-input" /></div>
            <div><Label>Contact number</Label><Input className="mt-1.5" value={f.contact_number} onChange={(e) => setF({ ...f, contact_number: e.target.value })} data-testid="ben-contact-input" /></div>
            <div><Label>Medical condition</Label><Textarea className="mt-1.5" value={f.medical_condition} onChange={(e) => setF({ ...f, medical_condition: e.target.value })} data-testid="ben-condition-input" /></div>
            <div>
              <Label>Service / delivery address</Label>
              <div className="mt-1.5">
                <AddressAutocomplete
                  value={f.service_address}
                  onChange={(v) => setF((p) => ({ ...p, service_address: v }))}
                  onResolved={(p) => setF((prev) => ({ ...prev, service_address: p.address || prev.service_address, geolocation: p.geolocation || prev.geolocation }))}
                  placeholder="Start typing the visit address…"
                  testId="ben-address-input"
                />
              </div>
              <LocationPin geolocation={f.geolocation} address={f.service_address} testId="ben-address-pin" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>City</Label><Input className="mt-1.5" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="ben-city-input" /></div>
              <div>
                <Label>State</Label>
                <select className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="ben-state-select">
                  <option value="">Select…</option>
                  {NG_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div><Label>Relationship to you</Label><Input className="mt-1.5" value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} data-testid="ben-relationship-input" /></div>
            <FileField label="Photograph (optional)" value={f.photograph_file_id} onChange={(v) => setF({ ...f, photograph_file_id: v })} purpose="BENEFICIARY_PHOTO" testId="ben-photo-upload" />
            <GeoField value={f.geolocation} onChange={(v) => setF({ ...f, geolocation: v })} testId="ben-geo-btn" />
            <Button className="w-full rounded-full bg-emerald-900" disabled={!valid} onClick={save} data-testid="ben-save-btn">
              {editId ? "Save changes" : "Add beneficiary"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!timeline} onOpenChange={(o) => !o && setTimeline(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Care timeline — {timeline?.beneficiary?.full_name}</DialogTitle>
          </DialogHeader>
          {timeline && (
            <div className="space-y-5" data-testid="beneficiary-timeline">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Visits", timeline.summary.total_visits, "timeline-total"],
                  ["Confirmed", timeline.summary.confirmed_visits, "timeline-confirmed"],
                  ["Upcoming", timeline.summary.upcoming_visits, "timeline-upcoming"],
                  ["Care value", naira(timeline.summary.total_care_value_ngn), "timeline-value"],
                ].map(([label, value, testId]) => (
                  <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">{label}</p>
                    <p className="mt-1 font-display text-lg font-extrabold text-slate-900" data-testid={testId}>{value}</p>
                  </div>
                ))}
              </div>

              {timeline.visits.length ? (
                <ol className="relative space-y-5 border-l border-slate-200 pl-6" data-testid="timeline-visits">
                  {timeline.visits.map((v) => (
                    <li key={v.booking_id} className="relative" data-testid={`timeline-visit-${v.booking_id}`}>
                      <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-600" />
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {new Date(v.scheduled_at).toLocaleString()}
                      </p>
                      <p className="mt-1 font-display font-bold text-slate-900">{v.service_name}</p>
                      <p className="text-sm text-slate-600">
                        {v.provider_name} · {naira(v.fee_ngn)} · {v.status.replaceAll("_", " ")}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Witness: {v.witness_name || "none"}
                        {v.confirmed_by ? ` · confirmed by ${v.confirmed_by.role.toLowerCase()}` : ""}
                      </p>
                      <ul className="mt-2 space-y-1 text-[11px] text-slate-400">
                        {v.events.map((ev, i) => (
                          <li key={i}>
                            {ev.action.replaceAll("_", " ")} · {new Date(ev.at).toLocaleString()}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-slate-500" data-testid="timeline-empty">
                  No care visits recorded for this beneficiary yet.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

const EMPTY_W = {
  full_name: "", phone: "", address: "", relationship: "", photograph_file_id: "",
  id_type: "", id_number: "", id_document_file_id: "", witness_form_file_id: "",
};

export function Witnesses() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY_W);

  const load = () => api.get("/api/witnesses").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...f, photograph_file_id: f.photograph_file_id || null, relationship: f.relationship || null };
    try {
      if (editId) await api.put(`/api/witnesses/${editId}`, payload);
      else await api.post("/api/witnesses", payload);
      toast.success(editId ? "Witness updated" : "Witness added");
      setOpen(false);
      setEditId(null);
      setF(EMPTY_W);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/witnesses/${id}`);
      toast.success("Witness removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const valid = f.full_name && f.phone && f.address && f.id_type && f.id_number && f.id_document_file_id && f.witness_form_file_id;

  return (
    <DashboardLayout title="Witnesses" subtitle="Trusted people who confirm a care visit took place. Witnesses do not need a CARENEST account." nav={NAV}>
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-5" data-testid="witness-form-banner">
        <FileText className="h-6 w-6 text-emerald-800" />
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold text-emerald-900">Witness Declaration Form</p>
          <p className="mt-1 text-sm text-emerald-800">
            Download the form, have your witness complete and sign it, then upload it here with a copy of their ID.
          </p>
        </div>
        <Button asChild variant="outline" className="rounded-full border-emerald-300">
          <a href="/witness-form.html" target="_blank" rel="noreferrer" data-testid="download-witness-form-btn">
            <Download className="mr-2 h-4 w-4" /> Download form
          </a>
        </Button>
      </div>
      <Panel
        title={`${rows.length} witness${rows.length === 1 ? "" : "es"}`}
        testId="witnesses-panel"
        action={
          <Button className="rounded-full bg-emerald-900 hover:bg-emerald-800" onClick={() => { setEditId(null); setF(EMPTY_W); setOpen(true); }} data-testid="add-witness-btn">
            <Plus className="mr-2 h-4 w-4" /> Add witness
          </Button>
        }
      >
        {rows.length ? (
          <ul className="divide-y divide-slate-100" data-testid="witnesses-list">
            {rows.map((w) => (
              <li key={w.id} className="flex flex-wrap items-start justify-between gap-4 py-5" data-testid={`witness-row-${w.id}`}>
                <div className="min-w-0">
                  <p className="font-display font-bold text-slate-900">{w.full_name}</p>
                  <p className="text-sm text-slate-600">{w.phone}{w.relationship ? ` · ${w.relationship}` : ""}</p>
                  <p className="mt-1 text-xs text-slate-400">{w.address}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditId(w.id); setF({ ...EMPTY_W, ...w }); setOpen(true); }} data-testid={`edit-witness-${w.id}`}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => remove(w.id)} data-testid={`remove-witness-${w.id}`}>Remove</Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No witnesses recorded yet.</p>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit witness" : "New witness"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input className="mt-1.5" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="wit-name-input" /></div>
            <div><Label>Phone number</Label><Input className="mt-1.5" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="wit-phone-input" /></div>
            <div><Label>Physical address</Label><Textarea className="mt-1.5" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="wit-address-input" /></div>
            <div><Label>Relationship (optional)</Label><Input className="mt-1.5" value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} data-testid="wit-relationship-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>ID type</Label>
                <select className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={f.id_type} onChange={(e) => setF({ ...f, id_type: e.target.value })} data-testid="wit-idtype-select">
                  <option value="">Select…</option>
                  {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div><Label>ID number</Label><Input className="mt-1.5" value={f.id_number} onChange={(e) => setF({ ...f, id_number: e.target.value })} data-testid="wit-idnumber-input" /></div>
            </div>
            <FileField label="Identification document" value={f.id_document_file_id} onChange={(v) => setF({ ...f, id_document_file_id: v })} purpose="WITNESS_ID" testId="wit-iddoc-upload" />
            <FileField label="Signed witness declaration form" hint="Upload the completed form you downloaded" value={f.witness_form_file_id} onChange={(v) => setF({ ...f, witness_form_file_id: v })} purpose="WITNESS_FORM" testId="wit-form-upload" />
            <FileField label="Photograph" value={f.photograph_file_id} onChange={(v) => setF({ ...f, photograph_file_id: v })} purpose="WITNESS_PHOTO" testId="wit-photo-upload" />
            <Button className="w-full rounded-full bg-emerald-900" disabled={!valid} onClick={save} data-testid="wit-save-btn">
              {editId ? "Save changes" : "Add witness"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
